import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { delay, Subscription, tap } from 'rxjs';
import { ConnectionService, ConnectionServiceOptions, ConnectionState } from 'ngx-connection-service';

// Import shared services
import { CommonService } from './core/services/common/common.service';
import { LoaderService } from './core/services/LoaderService/loader.service';
import { ToastService } from './core/services/toast/toast.service';

import { environment } from 'src/environments/environment';
import { MessagingService } from './core/services/messaging/messaging.service';
import { DeviceRegistrationService } from './core/services/device-registration/device-registration.service';
import { ChatBotService } from './core/services/chat-bot/chat-bot.service';
import { BillingServiceService } from './epics/Account/Billings/shared/services/billing-service.service';
import { HostListener } from '@angular/core';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {

    // Title of the Angular application
    title = 'fussion-app-v2';
    private lastHiddenTime = 0;
    private inactivityTimer: any;
    private readonly RELOAD_AFTER_MS = 1 * 60 * 1000; // 1 minutes

    // Observable to monitor and bind loading state across the app
    isLoading = this.loaderService.isLoading$
        .pipe(
            delay(0)   // 👈 force async emission
        );;

    // Network status - 'ONLINE' or 'OFFLINE'
    status!: string;

    // Current connection state returned by ng-connection-service
    currentState!: ConnectionState;

    // Master subscription container to clean up all observables
    private subscription = new Subscription();

    // Track whether the initial network status check is completed
    private initialNetworkCheckDone = false;

    // Routes where back button should be disabled
    private readonly protectedRoutes: string[] = [
        '/get-started/intro-survey',
        '/get-started/create-workspace',
        '/get-started/create-project',
        '/get-started/sdk-integration',
    ];

    modalTrigger: { trigger: string, flag: boolean; };
    isRemaingNotify: boolean;
    isExpireNotify: boolean;

    constructor(
        public commonService: CommonService,             // Shared utility methods
        private loaderService: LoaderService,            // Loading spinner service
        public toastService: ToastService,              // Toast notification service
        private router: Router,                          // Angular Router
        private connectionService: ConnectionService,    // Service to monitor internet connectivity
        private messagingService: MessagingService,
        private deviceService: DeviceRegistrationService,
        public _chat_bot: ChatBotService,
        private _billingService: BillingServiceService,
    ) {

        this._chat_bot.modalState$.subscribe(state => {
            if (state) {
                this.modalTrigger = state;
            }
        });

        this._billingService.get_is_Expire_Remaining_Notify()
            .subscribe((res) => {
                this.isRemaingNotify = res;
            });

        this._billingService.get_is_Expire_Notify()
            .subscribe((res) => {
                this.isExpireNotify = res;
            });
    }

    /**
     * Lifecycle hook called once component is initialized
     */
    ngOnInit(): void {
        console.log('>>>>>>>>>>>>> Build Version : 2.5.6 >>>>>>>>>>>>>>');

        // Start observing network connection
        this.monitorNetworkStatus();

        // New: check slow connection
        this.checkConnectionQuality();

        // Apply route-based back navigation blocking
        this.setupBackNavigationControl();

        // Set up message listener (will wait for service worker to be ready)
        // this.messagingService.listen().catch(err => {
        //     console.error('Failed to set up message listener:', err);
        // });

        // this.enableNotifications();

        this.startInactivityTimer();

        ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
            .forEach(event => {
                window.addEventListener(event, this.resetTimer.bind(this));
            });
    }


    async enableNotifications() {
        const result = await this.messagingService.requestPermission();
        if (!result) return;

        const { token, installationId } = result;
        console.log("result====>", result);
        const storedToken = localStorage.getItem('fcm_token');

        if (storedToken === token) {
            console.log('✅ Device already registered, skipping...');
            return;
        }

        // Register with backend
        // this.deviceService.registerDevice(token, installationId).subscribe({
        //     next: (res) => {
        //         console.log('Device registered ✅', res);
        //         localStorage.setItem('fcm_token', token); // Save for next check
        //     },
        //     error: (err) => console.error('Registration error ❌', err)
        // });
    }


    /**
     * Sets up the logic to block browser back button on certain onboarding routes.
     */
    private setupBackNavigationControl(): void {
        this.subscription.add(
            this.router.events.subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    const currentRoute = this.router.url;

                    // If user is on one of the protected routes
                    if (this.protectedRoutes.includes(currentRoute)) {
                        this.preventBackNavigation(); // Block back navigation
                    } else {
                        this.removeBackNavigationBlock(); // Allow normal behavior
                    }
                }
            })
        );
    }

    /**
     * Disables browser back navigation using `history.pushState`.
     * This is useful for steps like onboarding or surveys.
     */
    private preventBackNavigation(): void {
        history.pushState(null, '', location.href); // Push current URL to history stack
        window.onpopstate = () => {
            history.go(1); // Prevent going back by forcing forward navigation
        };
    }

    /**
     * Resets the `onpopstate` event to allow default back navigation.
     */
    private removeBackNavigationBlock(): void {
        window.onpopstate = null;
    }

    /**
     * Monitors the user's internet connection status in real time.
     * Uses ng-connection-service to detect status changes.
     * Shows toast messages only after the initial check.
     */
    private monitorNetworkStatus(): void {
        // const options: ConnectionServiceOptions = {
        //     enableHeartbeat: false,                          // Heartbeat disabled for custom API checks
        //     heartbeatUrl: environment.baseApiUrl,            // API endpoint to ping
        //     heartbeatInterval: 5000,                         // Not used since heartbeat is disabled
        //     heartbeatRetryInterval: 3000,
        //     requestMethod: 'get'
        // };
        this.connectionService.updateOptions({
            enableHeartbeat: false,
            heartbeatUrl: 'https://cloudflare.com/cdn-cgi/trace',
            heartbeatInterval: 1000,
            heartbeatRetryInterval: 1000,
            requestMethod: 'get'
        });

        this.subscription.add(
            this.connectionService.monitor(true).pipe(
                tap((newState: ConnectionState) => {
                    this.currentState = newState;

                    const wasOnline = this.status === 'ONLINE';
                    const isOnline = newState.hasNetworkConnection;

                    if (this.initialNetworkCheckDone) {
                        if (isOnline && !wasOnline) {  // Only log/show toast if status changed to online
                            this.status = 'ONLINE';
                            this.toastService.showToast(
                                'success',
                                'Online',
                                'You are back online. All services are available.'
                            );
                            this.checkConnectionQuality();
                            console.log('>>>>>>>>>>>>> ONLINE >>>>>>>>>>>>>>');
                        } else if (!isOnline && wasOnline) {  // Only log/show toast if status changed to offline
                            this.status = 'OFFLINE';
                            this.toastService.showToast(
                                'error',
                                'No Internet',
                                'You are offline. Some features may not work.'
                            );
                            console.log('>>>>>>>>>>>>> OFFLINE >>>>>>>>>>>>>>');
                        }
                        // If no change, do nothing
                    } else {
                        // Silent initial status set (no toast)
                        this.status = isOnline ? 'ONLINE' : 'OFFLINE';
                        this.initialNetworkCheckDone = true;
                        console.log('Initial network status:', this.status);
                    }
                })

            ).subscribe()
        );
    }

    /**
     * Checks connection quality by:
     * - Using Network Information API (if available)
     * - Measuring ping latency to API endpoint
     * Shows a warning toast if connection is detected slow.
     */
    private async checkConnectionQuality(): Promise<void> {
        // 1. Network Information API check
        // const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
        // if (connection && connection.effectiveType) {
        //     const slowTypes = ['slow-2g', '2g', '3g'];
        //     if (slowTypes.includes(connection.effectiveType)) {
        //         this.toastService.showToast('warning', 'Slow Internet', 'Your network connection is slow.');
        //         console.log('Network Information API detected slow connection:', connection.effectiveType);
        //     }
        // }

        // 2. Ping latency check
        const url = 'https://cloudflare.com/cdn-cgi/trace';

        const latency = await this.pingUrl(url);
        if (latency > 400) { // threshold in ms
            this.toastService.showToast(
                'warning',
                'Slow Internet',
                `High latency detected (${Math.round(latency)} ms). Performance may be affected.`
            );
            console.log('Ping latency detected slow connection:', Math.round(latency), 'ms');
        }
    }
    /**
    * Measures round-trip time of a HEAD request to a given URL.
    * Returns latency in milliseconds or Infinity on error.
    */
    private pingUrl(url: string): Promise<number> {
        const start = performance.now();
        return fetch(url, { method: 'HEAD', cache: 'no-cache' })
            .then(() => performance.now() - start)
            .catch(() => Infinity);
    }

    /**
     * Lifecycle hook called before the component is destroyed.
     * Used to unsubscribe all active subscriptions to avoid memory leaks.
     */
    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        clearTimeout(this.inactivityTimer);
    }

    /**
     * Placeholder for logging-related actions
     * (Can be extended later if needed)
     */
    showLogs(): void { }


    closeModal(trigger: string): void {
        this._chat_bot.close(trigger);
    }



    // User switches tabs/windows
    @HostListener('document:visibilitychange')
    onVisibilityChange() {
        if (document.hidden) {
            this.lastHiddenTime = Date.now();
        } else {
            const elapsed = Date.now() - this.lastHiddenTime;

            if (elapsed >= this.RELOAD_AFTER_MS) {
                window.location.reload();
            }
        }
    }

    private startInactivityTimer() {
        this.inactivityTimer = setTimeout(() => {
            window.location.reload();
        }, this.RELOAD_AFTER_MS);
    }

    private resetTimer() {
        clearTimeout(this.inactivityTimer);
        this.startInactivityTimer();
    }
}
